import { useEffect, useRef, useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import {
  DATE_PRESET_KEYS,
  DATE_PRESET_LABELS,
  describeDateRange
} from '../../utils/dateRangePresets';
import styles from './AdminFiltersModal.module.css';

/**
 * AdminFiltersModal — shared, config-driven Filters modal for admin list
 * pages (Support Tickets, Clients, Welcome Calls, AR, Abandoned Intakes).
 *
 * filters: array of filter defs, rendered in order:
 *   { key, label, type: 'select',      options: [{value,label}], placeholder? }
 *   { key, label, type: 'multiselect', options: [{value,label}] }
 *   { key, label, type: 'dateRange' }   // presets 1D/1W/1M/YTD + custom range
 * values: { [key]: value } — select: string; multiselect: string[];
 *         dateRange: { preset, start?, end? } (start/end as 'YYYY-MM-DD')
 * onApply(nextValues): Apply Filters pressed — parent persists + closes
 * onReset(): Reset / Clear All pressed — parent restores the unfiltered list
 *
 * Selects with zero options are skipped (options endpoints can 403 for
 * admins whose role lacks that module — the filter simply isn't offered).
 */

const hasValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Boolean(value.preset);
  return true;
};

// Number of active filters — drives the Filters button badge
export const countActiveFilters = (values) =>
  Object.values(values ?? {}).filter(hasValue).length;

// Chips for the active filters: [{ key, value?, label }]
export const buildFilterChips = (filterDefs, values) =>
  (filterDefs ?? []).flatMap((def) => {
    const value = values?.[def.key];
    if (!hasValue(value)) return [];
    if (def.type === 'dateRange') {
      const described = describeDateRange(value);
      return described ? [{ key: def.key, label: `${def.label}: ${described}` }] : [];
    }
    const labelFor = (v) =>
      def.options?.find((opt) => opt.value === v)?.label ?? v;
    if (def.type === 'multiselect') {
      return value.map((v) => ({
        key: def.key,
        value: v,
        label: `${def.label}: ${labelFor(v)}`
      }));
    }
    return [{ key: def.key, label: `${def.label}: ${labelFor(value)}` }];
  });

// Next values after removing one chip (multiselect removes just that value)
export const removeFilterValue = (values, chip) => {
  const { [chip.key]: current, ...rest } = values ?? {};
  if (chip.value !== undefined && Array.isArray(current)) {
    const remaining = current.filter((v) => v !== chip.value);
    return remaining.length > 0 ? { ...rest, [chip.key]: remaining } : rest;
  }
  return rest;
};

const FunnelIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

// "Filters" trigger with active-count badge — render in the page toolbar
export const FiltersButton = ({ activeCount = 0, onClick }) => (
  <button
    type="button"
    className={styles.filtersButton}
    onClick={onClick}
    aria-haspopup="dialog"
    aria-label={
      activeCount > 0 ? `Filters, ${activeCount} active` : 'Filters'
    }
  >
    <FunnelIcon />
    <span>Filters</span>
    {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
  </button>
);

// Removable active-filter chips — render under the page toolbar
export const FilterChips = ({ chips = [], onRemove }) => {
  if (chips.length === 0) return null;
  return (
    <div className={styles.chipsRow}>
      {chips.map((chip) => (
        <span key={`${chip.key}:${chip.value ?? ''}`} className={styles.chip}>
          <span>{chip.label}</span>
          <button
            type="button"
            onClick={() => onRemove(chip)}
            aria-label={`Remove filter ${chip.label}`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

const SelectField = ({ def, value, onChange, inputRef }) => (
  <div className={styles.field}>
    <label htmlFor={`filter-${def.key}`} className={styles.fieldLabel}>
      {def.label}
    </label>
    <select
      id={`filter-${def.key}`}
      ref={inputRef}
      className={styles.select}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{def.placeholder ?? 'Any'}</option>
      {def.options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const MultiselectField = ({ def, value, onChange, inputRef }) => {
  const selected = value ?? [];
  const toggle = (optValue) =>
    onChange(
      selected.includes(optValue)
        ? selected.filter((v) => v !== optValue)
        : [...selected, optValue]
    );
  return (
    <fieldset className={`${styles.field} ${styles.fieldset}`}>
      <legend className={styles.fieldLabel}>{def.label}</legend>
      <div className={styles.checkboxGroup}>
        {def.options.map((opt, index) => (
          <label key={opt.value} className={styles.checkboxOption}>
            <input
              type="checkbox"
              ref={index === 0 ? inputRef : undefined}
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

const DateRangeField = ({ def, value, onChange, inputRef }) => {
  const preset = value?.preset ?? '';
  const setPreset = (key) =>
    onChange(preset === key ? undefined : { ...(value ?? {}), preset: key });
  return (
    <fieldset className={`${styles.field} ${styles.fieldset}`}>
      <legend className={styles.fieldLabel}>{def.label}</legend>
      <div className={styles.presetRow} role="group" aria-label={`${def.label} presets`}>
        {DATE_PRESET_KEYS.map((key, index) => (
          <button
            key={key}
            type="button"
            ref={index === 0 ? inputRef : undefined}
            className={`${styles.presetBtn} ${preset === key ? styles.presetActive : ''}`}
            aria-pressed={preset === key}
            title={DATE_PRESET_LABELS[key]}
            onClick={() => setPreset(key)}
          >
            {key === 'CUSTOM' ? 'Custom' : key}
          </button>
        ))}
      </div>
      {preset === 'CUSTOM' && (
        <div className={styles.dateRange}>
          <Input
            type="date"
            label="From"
            id={`filter-${def.key}-from`}
            value={value?.start ?? ''}
            max={value?.end || undefined}
            onChange={(e) => onChange({ ...value, start: e.target.value })}
          />
          <span className={styles.dateSeparator} aria-hidden="true">
            to
          </span>
          <Input
            type="date"
            label="To"
            id={`filter-${def.key}-to`}
            value={value?.end ?? ''}
            min={value?.start || undefined}
            onChange={(e) => onChange({ ...value, end: e.target.value })}
          />
        </div>
      )}
    </fieldset>
  );
};

const AdminFiltersModal = ({
  isOpen,
  onClose,
  title = 'Filters',
  filters = [],
  values = {},
  onApply,
  onReset
}) => {
  const [draft, setDraft] = useState(values);
  const firstFieldRef = useRef(null);

  // Re-seed the draft each time the modal opens; edits only persist on Apply
  useEffect(() => {
    if (isOpen) setDraft(values ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Move focus into the modal on open (base Modal handles Escape + overflow)
  useEffect(() => {
    if (isOpen) firstFieldRef.current?.focus();
  }, [isOpen]);

  const setValue = (key, value) =>
    setDraft((prev) => {
      const rest = Object.fromEntries(
        Object.entries(prev).filter(([entryKey]) => entryKey !== key)
      );
      return hasValue(value) ? { ...rest, [key]: value } : rest;
    });

  const handleApply = (e) => {
    e.preventDefault();
    onApply(draft);
  };

  const handleReset = () => {
    setDraft({});
    onReset();
  };

  const visibleDefs = filters.filter(
    (def) => def.type === 'dateRange' || (def.options?.length ?? 0) > 0
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="medium">
      <form onSubmit={handleApply} className={styles.form}>
        <div className={styles.fields}>
          {visibleDefs.map((def, index) => {
            const inputRef = index === 0 ? firstFieldRef : undefined;
            const FieldComponent =
              def.type === 'dateRange'
                ? DateRangeField
                : def.type === 'multiselect'
                  ? MultiselectField
                  : SelectField;
            return (
              <FieldComponent
                key={def.key}
                def={def}
                value={draft[def.key]}
                onChange={(v) => setValue(def.key, v)}
                inputRef={inputRef}
              />
            );
          })}
        </div>
        <div className={styles.footer}>
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset / Clear All
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminFiltersModal;
